<template>
  <div class="search-panel">
    <div class="search-panel__main">
      <p class="search-panel__title title" @click="visibleFiltersSection = !visibleFiltersSection">
        Поиск в разделах
        <span v-if="searchStore.filtersSection" class="title_green">
          Указаны {{ searchStore.filtersSection }} параметр
        </span>
        <span v-else class="title_green">Не указаны</span>
        <ArrowIcon
          class="title__arrow"
          :class="{ 'title__arrow_active': visibleFiltersSection }"
        />
      </p>
      <SearchBySection />
      <transition name="fade-slide">
        <div v-show="visibleFiltersSection" class="filters-container">
          <SearchByPhysicalData />
          <PriceBlock />
        </div>
      </transition>
    </div>
    <div class="search-panel__profiles">
      <ProfileBlock
        :visibleFiltersProfiles="visibleFiltersProfiles"
        @toggleFilter="visibleFiltersProfiles = !visibleFiltersProfiles"
      />
    </div>
    <div class="search-panel__appearance">
      <AppearanceBlock
        :visibleFiltersAppearance="visibleFiltersAppearance"
        @toggleFilter="visibleFiltersAppearance = !visibleFiltersAppearance"
      />
    </div>
    <div class="search-panel__location">
      <LocationBlock
        :visibleFiltersLocation="visibleFiltersLocation"
        @toggleFilter="visibleFiltersLocation = !visibleFiltersLocation"
      />
    </div>
    <div class="search-panel__additional-options">
      <AdditionalOptionsBlock
        :visibleFiltersAdditionalOptions="visibleFiltersAdditionalOptions"
        @toggleFilter="visibleFiltersAdditionalOptions = !visibleFiltersAdditionalOptions"
      />
    </div>
    <div class="search-panel__search">
      <pButton
        class="search-panel__reset"
        @click="searchStore.resetOptions"
      >
        Сбросить все параметры
      </pButton>
      <pButton
        class="search-panel__search-btn"
        @click="searchProfiles"
      >
        Искать
      </pButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, Ref, ref } from "vue";
import SearchBySection from "@/searchSection/components/SearchPanel/SearchBySection.vue";
import SearchByPhysicalData from "@/searchSection/components/SearchPanel/SearchByPhysicalData.vue";
import PriceBlock from "@/searchSection/components/SearchPanel/PriceBlock.vue";
import ProfileBlock from "@/searchSection/components/SearchPanel/ProfileBlock.vue";
import AppearanceBlock from "@/searchSection/components/SearchPanel/AppearanceBlock.vue";
import LocationBlock from "@/searchSection/components/SearchPanel/LocationBlock.vue";
import AdditionalOptionsBlock from "@/searchSection/components/SearchPanel/AdditionalOptionsBlock.vue";
import pButton from '@/common/pButton/pButton.vue'
import { useSearchStore } from "@/searchSection/store/searchStore";
import ProfileCardInterface from "@/common/types/ProfileCardInterface";
import { useResultsStore } from "@/searchSection/store/resultsStore";
import ArrowIcon from "@/common/icons/ArrowIcon.vue";
import axios from "axios";

const searchStore = useSearchStore()
const resultsStore = useResultsStore()

const visibleFiltersSection: Ref<boolean> = ref(true)
const visibleFiltersProfiles: Ref<boolean> = ref(true)
const visibleFiltersAppearance: Ref<boolean> = ref(true)
const visibleFiltersLocation: Ref<boolean> = ref(true)
const visibleFiltersAdditionalOptions: Ref<boolean> = ref(true)

async function searchProfiles(): Promise<void> {
  try {
    visibleFiltersAdditionalOptions.value = false

    resultsStore.setResultsLoading(true)
    const cards: ProfileCardInterface[] = await searchStore.searchProfiles()
    resultsStore.setResults(cards)
    setTimeout(() => {
        document.querySelector('.search-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
  } catch (e: unknown) {
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 400) {
        window.showNotification(e.response.data?.error?.message || 'Произошла ошибка!', 'error')
      } else {
        window.showNotification('Произошла ошибка на сервере!', 'error')
      }
    } else {
      window.showNotification('Произошла непредвиденная ошибка!', 'error')
    }
  } finally {
    resultsStore.setResultsLoading(false)
  }
}

onMounted(() => {
  searchStore.setDefaultOptions()
})
</script>

<style lang="scss">
.title {
  display: flex;
  align-items: center;

  &_green {
    background: #59EC34;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    padding: 3.5px 6px;
    border-radius: 8px;
    margin-left: 6px;
    position: relative;
  }

  &__arrow {
    cursor: pointer;
    margin-left: auto;
    transition: transform .3s ease;

    &_active {
      transform: rotate(180deg);
    }
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s linear, transform 0.2s linear;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

<style scoped lang="scss">
.search-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 1fr;
  gap: 20px;
  align-items: start;

  @media screen and (max-width: 1350px) {
    grid-template-columns: 1fr;
    grid-template-rows: initial;
  }

  &__main,
  &__profiles,
  &__appearance,
  &__search,
  &__location,
  &__additional-options {
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(30, 32, 35, 1);
  }

  &__main {
    grid-row: 1/3;
  }

  &__appearance {
    grid-column: 2/3;
    grid-row: 2/3;

    @media screen and (max-width: 1350px) {
      grid-column: initial;
      grid-row: initial;
    }
  }

  &__reset {
    font-weight: bold;
    background: #FFED481A;
    border: none;
    color: #FFED48;
    width: 22%;

    @media screen and (max-width: 1350px) {
      width: auto;
    }
  }

  &__search-btn {
    font-weight: 600;
    width: 76%;

    @media screen and (max-width: 1350px) {
      width: auto;
    }
  }

  &__search {
    grid-column: 1/3;
    display: flex;
    gap: 20px;

    @media screen and (max-width: 1350px) {
      grid-column: 1;
      flex-direction: column;
    }
  }

  &__location, &__additional-options {
    grid-column: 1/3;

    @media screen and (max-width: 1350px) {
      grid-column: initial;
    }
  }
}
</style>